import React, { useEffect, useState } from 'react';
import {
	createBrowserRouter,
	Navigate,
	Outlet,
	RouterProvider,
	useLocation,
} from 'react-router-dom';
import { VisualEditing } from '@sanity/visual-editing/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { HelpBlogPage } from '@dealstack/frontend/app/pages/HelpBlog/HelpBlogPage/HelpBlogPage';
import { ArticlePage } from '@dealstack/frontend/app/pages/HelpBlog/ArticlePage/ArticlePage';
import { CategoryPage } from '@dealstack/frontend/app/pages/HelpBlog/CategoryPage/CategoryPage';
import { AuthorPage } from '@dealstack/frontend/app/pages/HelpBlog/AuthorPage/AuthorPage';
import { CategoriesPage } from '@dealstack/frontend/app/pages/HelpBlog/CategoriesPage/CategoriesPage';
import { AuthorsPage } from '@dealstack/frontend/app/pages/HelpBlog/AuthorsPage/AuthorsPage';
import { BreadcrumbsProvider } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import {
	HelpBlogContext,
	useHelpBlogController,
} from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogController';
import type { ClientPerspective } from '@sanity/client';
import { HELP_BLOG_ROUTES } from '@dealstack/frontend/shared/shared-routes.ts';
import { API_LAYER_LOCAL_STORAGE_KEYS } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import { ApplicationType } from '@dealstack/shared/lib/generated';

enum SANITY_SESSION_STORAGE_KEYS {
	SANITY_PERSPECTIVE = 'SANITY_PERSPECTIVE',
	SANITY_DATASET = 'SANITY_DATASET',
}

const HelpBlogWrapper = () => {
	const [perspective, setPerspectiveState] = useState<ClientPerspective>(
		() =>
			(sessionStorage.getItem(
				SANITY_SESSION_STORAGE_KEYS.SANITY_PERSPECTIVE
			) as ClientPerspective) || 'drafts'
	);
	const [dataset, setDatasetState] = useState<ApplicationType>(
		() =>
			(sessionStorage.getItem(
				SANITY_SESSION_STORAGE_KEYS.SANITY_DATASET
			) as ApplicationType) || ApplicationType.DEALSTACK
	);
	const queryClient = useQueryClient();
	const location = useLocation();
	const helpBlogController = useHelpBlogController(dataset as ApplicationType, perspective);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const perspectiveParam = params.get('sanity-preview-perspective');
		const accessToken = params.get('sanityPreviewToken');
		const datasetParam = params.get('sanityDataset');

		if (accessToken) {
			sessionStorage.setItem(API_LAYER_LOCAL_STORAGE_KEYS.SANITY_PREVIEW_TOKEN, accessToken);
		}

		if (perspectiveParam) {
			setPerspective(perspectiveParam as ClientPerspective);
		}

		if (datasetParam) {
			setDataset(datasetParam as ApplicationType);
		}
	}, [location.search]);

	const handleRefresh = async () => {
		// Invalidate all help blog queries by matching any query that starts with /help-blog
		await queryClient.invalidateQueries({
			predicate: (query) => {
				const firstKey = query.queryKey[0];
				return typeof firstKey === 'string' && firstKey.startsWith('/help-blog');
			},
		});
	};

	const setPerspective = (perspective: ClientPerspective) => {
		sessionStorage.setItem(
			SANITY_SESSION_STORAGE_KEYS.SANITY_PERSPECTIVE,
			perspective as string
		);
		setPerspectiveState(perspective);
	};

	const setDataset = (dataset: ApplicationType) => {
		sessionStorage.setItem(SANITY_SESSION_STORAGE_KEYS.SANITY_DATASET, dataset);
		setDatasetState(dataset);
	};

	return (
		<HelpBlogContext.Provider value={helpBlogController}>
			<BreadcrumbsProvider>
				<Outlet />
				<VisualEditing refresh={handleRefresh} />
			</BreadcrumbsProvider>
		</HelpBlogContext.Provider>
	);
};

const previewRouter = createBrowserRouter([
	{
		path: HELP_BLOG_ROUTES.preview,
		element: <HelpBlogWrapper />,
		children: [
			{
				path: HELP_BLOG_ROUTES.CATEGORIES,
				element: <CategoriesPage />,
			},
			{
				path: HELP_BLOG_ROUTES.AUTHORS,
				element: <AuthorsPage />,
			},
			{
				path: HELP_BLOG_ROUTES.CATEGORY,
				element: <CategoryPage />,
			},
			{
				path: HELP_BLOG_ROUTES.AUTHOR,
				element: <AuthorPage />,
			},
			{
				path: HELP_BLOG_ROUTES.ARTICLE,
				element: <ArticlePage />,
			},
			{
				index: true,
				element: <HelpBlogPage />,
			},
		],
	},
	{
		path: '*',
		element: <Navigate to="/preview/help" replace />,
	},
]);

export const HelpBlogPreview = () => {
	return <RouterProvider router={previewRouter} useTransitions={false} />;
};
